import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { RefreshFailed } from '@/components/RefreshFailed'
import { settingsCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Disclosure } from '@/ui/Disclosure/Disclosure'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Input, Select } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { gatewayKeysApi } from '../../api/workspaces'
import { useMutationToast } from '../../hooks/useMutationToast'
import { useTopicsQuery } from '../../hooks/useTopicsQuery'
import type { GatewayKeyInfo } from '../../types/api'
import { formatDate } from '../../utils/time'
import styles from './KeysTab.module.css'

export function KeysTab({ wsId }: { wsId: string }) {
  const qc = useQueryClient()
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyTopicId, setNewKeyTopicId] = useState('')
  const [newKeyModelOverride, setNewKeyModelOverride] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [adding, setAdding] = useState(false)
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const keysQuery = useQuery<GatewayKeyInfo[]>({
    queryKey: ['gateway-keys', wsId],
    queryFn: () => gatewayKeysApi.list(wsId),
    enabled: !!wsId,
    staleTime: 30_000,
  })
  const showSkeleton = useSkeletonDelay(keysQuery.isPending)
  const keys = keysQuery.data ?? []

  // Topic scope is visible, so topics load with the form (D129).
  const { data: topics = [] } = useTopicsQuery(wsId)

  const createMut = useMutationToast({
    mutationFn: () =>
      gatewayKeysApi.create(wsId, newKeyName.trim(), {
        topic_id: newKeyTopicId || undefined,
        model_override: newKeyModelOverride.trim() || undefined,
      }),
    onSuccess: (res) => {
      setCreatedKey(res.raw_key)
      closeForm()
      void qc.invalidateQueries({ queryKey: ['gateway-keys', wsId] })
    },
    errorMessage: settingsCopy.keys.createFailed,
  })

  const revokeMut = useMutationToast({
    mutationFn: (keyId: string) => gatewayKeysApi.revoke(wsId, keyId),
    onSuccess: () => {
      setConfirmRevokeId(null)
      void qc.invalidateQueries({ queryKey: ['gateway-keys', wsId] })
    },
    successMessage: settingsCopy.keys.revoked,
    errorMessage: settingsCopy.keys.revokeFailed,
  })

  function closeForm() {
    setAdding(false)
    setNewKeyName('')
    setNewKeyTopicId('')
    setNewKeyModelOverride('')
    setShowAdvanced(false)
  }

  function copy(text: string, id: string) {
    void navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const keyToRevoke = keys.find((k) => k.id === confirmRevokeId)

  const addKey = (
    <Button variant="ghost" onClick={() => setAdding(true)}>
      <Icon name="plus" size="icon-embedded" />
      {settingsCopy.keys.add}
    </Button>
  )
  const createForm = (
    <form
      aria-label={settingsCopy.keys.add}
      onSubmit={(e) => {
        e.preventDefault()
        if (newKeyName.trim()) createMut.mutate()
      }}
    >
      <Stack gap="space-12">
        <div className={styles.keyRow}>
          <Field label={settingsCopy.keys.name} className={styles.cell}>
            <Input
              autoFocus
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder={settingsCopy.keys.name}
            />
          </Field>
          <Field
            label={settingsCopy.keys.topicScope}
            hint={settingsCopy.keys.optional}
            help={settingsCopy.keys.topicScopeHelp}
            className={styles.cell}
          >
            <Select
              value={newKeyTopicId}
              onChange={(e) => setNewKeyTopicId(e.target.value)}
            >
              <option value="">{settingsCopy.keys.allTopics}</option>
              {topics.map((t) => (
                <option key={t.topic_id} value={t.topic_id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Inline gap="space-8" wrap={false} className={styles.submit}>
            <Button
              type="submit"
              disabled={!newKeyName.trim()}
              loading={createMut.isPending}
            >
              {settingsCopy.keys.create}
            </Button>
            <Button type="button" variant="outline" onClick={closeForm}>
              {settingsCopy.keys.cancel}
            </Button>
          </Inline>
        </div>

        <Disclosure
          label={
            showAdvanced
              ? settingsCopy.keys.hideOptions
              : settingsCopy.keys.showOptions
          }
          open={showAdvanced}
          onOpenChange={setShowAdvanced}
        >
          <Field
            label={settingsCopy.keys.modelOverride}
            hint={settingsCopy.keys.optional}
            className={styles.field}
          >
            <Input
              value={newKeyModelOverride}
              onChange={(e) => setNewKeyModelOverride(e.target.value)}
            />
          </Field>
        </Disclosure>
      </Stack>
    </form>
  )

  return (
    <section>
      <SectionHeader rule trailingSpacing="compact">
        {settingsCopy.keys.heading}
      </SectionHeader>
      <Stack gap="space-12">
        <Text as="p" tone="text-mute">
          {settingsCopy.keys.description}
        </Text>

        {createdKey && (
          <Stack gap="space-8">
            <Alert tone="success">{settingsCopy.keys.created}</Alert>
            <div className={styles.created}>
              <Field
                label={settingsCopy.newKey}
                readOnly
                className={styles.cell}
              >
                <Input mono readOnly value={createdKey} />
              </Field>
              <Button
                variant="outline"
                className={styles.rowAction}
                onClick={() => copy(createdKey, 'new')}
              >
                <Icon name="copy" size="icon-embedded" />
                {copiedId === 'new'
                  ? settingsCopy.keys.copied
                  : settingsCopy.keys.copy}
              </Button>
              <Button
                variant="quiet"
                className={styles.rowAction}
                onClick={() => setCreatedKey(null)}
              >
                {settingsCopy.keys.dismiss}
              </Button>
            </div>
          </Stack>
        )}

        <Stack gap="space-16">
          <div aria-busy={keysQuery.isPending}>
            {keysQuery.isPending ? (
              showSkeleton && <Skeleton lines={3} />
            ) : keysQuery.isError && !keysQuery.data ? (
              <Alert tone="danger">
                <Inline gap="space-8" align="baseline" wrap>
                  <Text>{settingsCopy.loadFailed.keys}</Text>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void keysQuery.refetch()}
                  >
                    {settingsCopy.retry}
                  </Button>
                </Inline>
              </Alert>
            ) : (
              <Stack gap="space-12">
                {keysQuery.isError && (
                  <RefreshFailed
                    message={settingsCopy.refreshFailed.keys}
                    onRetry={() => void keysQuery.refetch()}
                  />
                )}
                {keys.length === 0 && !adding && (
                  <EmptyState
                    variant="inline"
                    description={settingsCopy.keys.empty}
                  />
                )}
                <RowList aria-label={settingsCopy.keys.listLabel}>
                  {keys.map((k) => (
                    <RowListItem key={k.id}>
                      <Stack gap="space-4" className={styles.main}>
                        <Text>{k.name}</Text>
                        <Text variant="caption" tone="text-mute" numeric>
                          {[
                            k.last_used_at
                              ? settingsCopy.keys.lastUsed(
                                  formatDate(k.last_used_at),
                                )
                              : settingsCopy.keys.neverUsed,
                            k.model_override &&
                              settingsCopy.keys.model(k.model_override),
                            k.topic_id && settingsCopy.keys.scopedToTopic,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                      </Stack>
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmRevokeId(k.id)}
                      >
                        {settingsCopy.keys.revoke}
                      </Button>
                    </RowListItem>
                  ))}
                  {adding ? (
                    <RowListItem className={styles.form}>
                      {createForm}
                    </RowListItem>
                  ) : (
                    <RowListItem>{addKey}</RowListItem>
                  )}
                </RowList>
              </Stack>
            )}
          </div>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={confirmRevokeId !== null}
        title={settingsCopy.keys.revokeDialog.title}
        tone="danger"
        description={
          keyToRevoke
            ? settingsCopy.keys.revokeDialog.description(keyToRevoke.name)
            : undefined
        }
        confirmLabel={settingsCopy.keys.revokeDialog.confirm}
        onConfirm={() => {
          if (confirmRevokeId) revokeMut.mutate(confirmRevokeId)
        }}
        onClose={() => setConfirmRevokeId(null)}
        loading={revokeMut.isPending}
      />
    </section>
  )
}
